import { cx } from '@twind/core';
import Spinner from 'app/components/Spinner';
import ContactFormItemFactory from 'app/modules/contact-form/components/ContactFormItemFactory';
import useContactFormLogic from 'app/modules/contact-form/hooks/useContactFormLogic';
import useVirtualAssistantAvatar from 'app/modules/virtual-assistant/hooks/useVirtualAssistantAvatar';
import { ChatItemForm } from 'app/services/chatBot';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import BaseChatItem from './BaseChatItem';

export default function ContactFormChatItem({
  hideAvatar,
  onClickCancel,
  onComplete,
  chatItem,
}: {
  hideAvatar?: boolean;
  chatItem: ChatItemForm;
  onClickCancel: () => void;
  onComplete: () => void;
}) {
  const [isCancelled, setIsCancelled] = useState(false);
  const avatarURL = useVirtualAssistantAvatar();
  const location = useLocation();
  const openedThrough = location.state?.openedThrough ?? 'vaSingleChoiceOptionClick';

  const {
    isFetching,
    isPending,
    currentForm,
    currentPage,
    isComplete,
    submitted,
    isValid,
    hasErrors,
    handleFormSubmit,
    setValidity,
    dirtyFields,
    setFieldDirty,
  } = useContactFormLogic({
    onComplete,
    contactFormSlug: chatItem.ref_slug,
    openedThrough,
  });

  const handleCancel = useCallback(() => {
    setIsCancelled(true);
    onClickCancel();
  }, [onClickCancel]);

  if (isFetching) {
    return (
      <div className={`flex grow w-full items-center justify-center`}>
        <Spinner />
      </div>
    );
  }

  if (!currentForm || isCancelled) {
    return null;
  }

  return (
    <BaseChatItem
      avatarURL={avatarURL}
      showAvatar={!hideAvatar}
    >
      <form
        id="contact-form-chat-item"
        key={currentForm.ref_id}
        noValidate
        onSubmit={handleFormSubmit}
        ref={(form) => form && setValidity(form.checkValidity())}
        onChange={({ currentTarget: form }) => setValidity(form.checkValidity())}
        className="flex flex-col gap-4 h-full [&_h1]:(text-sm font-normal)"
      >
        <AnimatePresence mode="wait">
          <motion.div
            className={cx('flex flex-col gap-4 h-full', isComplete || isCancelled ? 'pb-0' : 'pb-28')}
            initial={currentPage === 1 ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {currentForm.items.map((item) => (
              <ContactFormItemFactory
                key={item.ref_id}
                item={item}
                isSubmitted={submitted}
                hasErrors={hasErrors}
                isValid={isValid}
                isFetching={isPending}
                dirtyFields={dirtyFields}
                setFieldDirty={setFieldDirty}
                buttonVariant="convoai"
                onClickCancel={handleCancel}
                isComplete={isComplete}
                showSpacer={false}
                contactFormSlug={chatItem.ref_slug}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </form>
    </BaseChatItem>
  );
}
